import { makeStyles } from '@material-ui/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Dropdown from 'core/elements/dropdown'
import Input from 'core/elements/input/Input'
import Tooltip from 'core/elements/tooltip'
import type Theme from 'core/themes/model'
import { debounce } from 'utils/async'
import IconButton from '../button/IconButton'

export interface SearchFieldOption {
  value: string
  label: string
}

export interface GridSearchFilterProps {
  value: string
  onChange: (value: string) => void
  label?: string
  searchFields?: SearchFieldOption[]
  onFieldChange?: (field: string) => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  searchBar: {
    outline: 'none',
    border: 0,
    margin: 0,
    marginRight: theme.spacing(1),
    '& > .inputFrame': {
      maxWidth: 120,
      padding: 0,
      backgroundColor: 'inherit',
      color: 'inherit',
    },
    '& .label, & .input, & .icon': {
      backgroundColor: 'inherit',
      color: 'inherit',
    },
  },
  clearIcon: {
    color: theme.palette.grey[700],
    cursor: 'pointer',
    marginRight: theme.spacing(1),

    '&:hover': {
      color: theme.palette.grey[900],
    },
    '&:active': {
      color: theme.palette.grey[700],
    },
  },
  searchInputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  groupedInput: {
    minWidth: 200,
    flex: 1,
    gap: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    '& .inputFrame': {
      borderWidth: 0,
      borderRadius: 0,
      minHeight: 34,
    },
    '& input': {
      minHeight: 34,
    },
  },
  groupedDropdown: {
    minWidth: 140,
    '& ul': {
      left: '-1px',
    },
    '& .inputFrame': {
      borderWidth: 0,
      borderRadius: 0,
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginTop: 0,
      minHeight: 34,
    },
  },
  searchButton: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    '& button': {
      borderWidth: 0,
      borderRadius: 0,
      height: 34,
      padding: '0 12px',
    },
  },
}))

export default function GridSearchFilter({
  value: initialValue,
  onChange,
  label = 'Search',
  searchFields,
  onFieldChange,
}: GridSearchFilterProps) {
  const classes = useStyles()
  const [value, setValue] = useState<string>(initialValue)
  const [selectedField, setSelectedField] = useState<string>('all')
  const valueRef = useRef(value)
  const debouncedUpdateFilterValue = useMemo(() => debounce<[string], void>(onChange), [onChange])

  useEffect(() => {
    valueRef.current = value
  }, [value])

  const handleOnChange = useCallback(
    async ({ target: { value } }) => {
      setValue(value)
      return debouncedUpdateFilterValue(value)
    },
    [debouncedUpdateFilterValue],
  )

  useEffect(() => {
    return () => {
      debouncedUpdateFilterValue.cancel()
    }
  }, [debouncedUpdateFilterValue])

  const handleFieldChange = useCallback(
    (field: string) => {
      setSelectedField(field)
      onFieldChange?.(field)
      // Re-trigger filter with current value so equalityComparerFn picks up new field
      debouncedUpdateFilterValue(valueRef.current ?? '')
    },
    [onFieldChange, debouncedUpdateFilterValue],
  )

  const renderClearIcon = useMemo(
    () => (
      <Tooltip message="Clear Search" align={{ vertical: 'top', horizontal: 'middle' }}>
        <FontAwesomeIcon
          className={classes.clearIcon}
          onClick={() => handleOnChange({ target: { value: '' } })}
          size="md"
          data-testid="clear-search-button"
        >
          times
        </FontAwesomeIcon>
      </Tooltip>
    ),
    [classes.clearIcon, handleOnChange],
  )

  if (searchFields?.length > 0) {
    const allFieldsOption = { value: 'all', label: 'All Fields' }
    const fieldOptions = [allFieldsOption, ...searchFields]

    return (
      <div className={classes.searchInputGroup}>
        <Input
          compact
          placeholder={label}
          className={classes.groupedInput}
          value={value !== undefined ? value : ''}
          onChange={handleOnChange}
          type="search"
          endAdornment={value?.length > 0 ? renderClearIcon : null}
        />
        <Dropdown
          className={classes.groupedDropdown}
          items={fieldOptions}
          value={selectedField}
          onChange={(field) => handleFieldChange(field as string)}
        />
        <div className={classes.searchButton}>
          <IconButton
            onClick={onChange.bind(null, valueRef.current ?? '')}
            disabled={!value?.trim()}
            size="md"
            info="Search"
            icon="search"
          />
        </div>
      </div>
    )
  }

  return (
    <Input
      compact
      icon="search"
      placeholder={label}
      className={classes.searchBar}
      value={value !== undefined ? value : ''}
      onChange={handleOnChange}
      type="search"
      endAdornment={value?.length > 0 ? renderClearIcon : null}
    />
  )
}
